import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';

function clearTypedFormationDate() {
  try {
    sessionStorage.removeItem('formationDateEdits');
  } catch (error) {
    // The sign-in page still opens if the browser refuses storage.
  }
}

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const started = useRef(false);

  const safeNext = (path) => (
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : '/dashboard'
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const authenticate = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state') || '/dashboard';
        const errorParam = searchParams.get('error');

        if (errorParam) {
          setError('Google authentication was cancelled or failed');
          setLoading(false);
          clearTypedFormationDate();
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        if (!code) {
          setError('No authorization code received from Google');
          setLoading(false);
          clearTypedFormationDate();
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        clearTypedFormationDate();
        const result = await handleGoogleCallback(code, state);

        if (result.requires2FA) {
          navigate('/login');
          return;
        }
        if (result.success) {
          window.location.assign(safeNext(result.redirect));
        } else {
          setError(result.error || 'Authentication failed');
          setLoading(false);
          clearTypedFormationDate();
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err) {
        console.error('Google callback error:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
        setError('An error occurred during authentication');
        setLoading(false);
        clearTypedFormationDate();
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    authenticate();
  }, [searchParams, navigate, handleGoogleCallback]);

  if (loading) {
    return (
      <ErrorBoundary section="Google Callback">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Completing Google sign-in...</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary section="Google Callback">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="bg-white p-8 shadow rounded-lg">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="mt-4 text-xl font-bold text-gray-900">Authentication Failed</h2>
                <p className="mt-2 text-sm text-gray-600" role="alert">{error}</p>
                <p className="mt-4 text-sm text-gray-500">Redirecting to login page...</p>
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary section="Google Callback">
      {null}
    </ErrorBoundary>
  );
};

export default GoogleCallback;

